import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import {
  Calendar, Book, CheckCircle, Users, Play, MapPin,
  ChevronDown, ChevronRight, Search, RotateCcw, Circle, X,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_META = {
  completed:    { label: 'Completed',   Icon: CheckCircle, text: 'text-green-400',  border: 'border-green-500/30',  bg: 'bg-green-500/10'  },
  in_progress:  { label: 'In Progress', Icon: Play,        text: 'text-cinema-glow', border: 'border-cinema-glow/30', bg: 'bg-cinema-glow/10' },
  not_started:  { label: 'Not Started', Icon: Circle,      text: 'text-cinema-muted', border: 'border-white/10',      bg: 'bg-white/5'       },
}

const eventTypeAccent = (t) => ({
  class:   { border: 'border-blue-500/30',   text: 'text-blue-400',   bg: 'bg-blue-500/10',   bar: 'bg-blue-500'   },
  exam:    { border: 'border-red-500/30',    text: 'text-red-400',    bg: 'bg-red-500/10',    bar: 'bg-red-500'    },
  meeting: { border: 'border-green-500/30',  text: 'text-green-400',  bg: 'bg-green-500/10',  bar: 'bg-green-500'  },
  holiday: { border: 'border-purple-500/30', text: 'text-purple-400', bg: 'bg-purple-500/10', bar: 'bg-purple-500' },
}[t] || { border: 'border-white/10', text: 'text-cinema-muted', bg: 'bg-white/5', bar: 'bg-white/20' })

// Format a lesson-progress row (from /progress/course) into the shape used by
// schedule.current_lesson / next_lesson so the collapsed row can recompute after a toggle.
const formatLessonFromDetail = (l) => ({
  lesson_id:        l.lesson_id,
  lesson_title:     l.lesson_title,
  lesson_number:    l.lesson_number,
  day_number:       l.day_number,
  lesson_type:      l.lesson_type,
  duration_minutes: l.duration_minutes,
  module_title:     l.module_title,
  week_number:      l.week_number,
  progress_status:  l.status || 'not_started',
})

// Recompute a schedule's summary fields from a full lesson list (used after an
// optimistic toggle in the expanded view, where we have all lessons).
const recomputeFromLessons = (schedule, lessons) => {
  const completed = lessons.filter(l => l.status === 'completed').length
  const total = lessons.length
  const inProgress = lessons.find(l => l.status === 'in_progress')
  const notStarted = lessons.find(l => !l.status || l.status === 'not_started')
  const rest = lessons.filter(l => !l.status || l.status === 'not_started').slice(1, 6)
  return {
    ...schedule,
    completed_lessons: completed,
    total_lessons: total,
    current_lesson: inProgress ? formatLessonFromDetail(inProgress) : null,
    next_lesson: notStarted ? formatLessonFromDetail(notStarted) : null,
    upcoming_lessons: rest.map(formatLessonFromDetail),
  }
}

// Best-effort optimistic update for a collapsed-row toggle (no full lesson list).
const applyCollapsedToggle = (schedule, lessonId, markComplete) => {
  if (markComplete) {
    const completed_lessons = (schedule.completed_lessons || 0) + 1
    let { current_lesson, next_lesson, upcoming_lessons } = schedule
    const up = upcoming_lessons || []
    if (current_lesson?.lesson_id === lessonId) {
      current_lesson = next_lesson || null
      next_lesson = up[0] || null
      upcoming_lessons = up.slice(1)
    } else if (next_lesson?.lesson_id === lessonId) {
      next_lesson = up[0] || null
      upcoming_lessons = up.slice(1)
    }
    return { ...schedule, completed_lessons, current_lesson, next_lesson, upcoming_lessons }
  }
  // Undo from collapsed view is rare (undo UI lives in expanded view); just decrement.
  return {
    ...schedule,
    completed_lessons: Math.max(0, (schedule.completed_lessons || 0) - 1),
  }
}

const pctOf = (s) => s.total_lessons > 0
  ? Math.round((s.completed_lessons / s.total_lessons) * 100)
  : 0

// ── Component ────────────────────────────────────────────────────────────────

export default function StudentProgress() {
  const [schedules, setSchedules] = useState([])
  const [trainerEvents, setTrainerEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [eventsDate, setEventsDate] = useState(new Date().toISOString().split('T')[0])
  const [eventsLoading, setEventsLoading] = useState(false)

  // Expandable per-student lesson detail
  const [expandedKey, setExpandedKey] = useState(null) // `${studentId}_${courseId}`
  const [lessonDetail, setLessonDetail] = useState({}) // { [key]: { loading, lessons, error } }

  // In-flight toggle tracking (Set of `${studentId}_${lessonId}`)
  const [toggling, setToggling] = useState(() => new Set())

  // Toolbar controls
  const [searchQuery, setSearchQuery] = useState('')
  const [courseFilter, setCourseFilter] = useState('all')
  const [sortBy, setSortBy] = useState('name') // 'name' | 'progress' | 'status'

  // Refs for focus management
  const rowHeaderRefs = useRef({})

  // ── Data fetching ──────────────────────────────────────────────────────────

  // Initial load: progress (date-independent) + today's events in one call.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const { data } = await api.get(`/schedule/daily/${eventsDate}`)
        if (cancelled) return
        setSchedules(data.schedules || [])
        setTrainerEvents(data.events || [])
      } catch (error) {
        console.error('Error fetching schedules:', error)
        toast.error('Failed to load student schedules')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- progress is date-independent; events fetched separately on date change

  // Date change: fetch events ONLY (progress is decoupled).
  useEffect(() => {
    if (loading) return // skip the initial-mount double-fetch
    let cancelled = false
    ;(async () => {
      setEventsLoading(true)
      try {
        const { data } = await api.get(`/schedule/events/${eventsDate}`)
        if (cancelled) return
        setTrainerEvents(data.events || [])
      } catch (error) {
        console.error('Error fetching events:', error)
        // Non-fatal — keep old events
      } finally {
        if (!cancelled) setEventsLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [eventsDate, loading])

  const fetchLessonDetail = useCallback(async (studentId, courseId) => {
    const key = `${studentId}_${courseId}`
    setLessonDetail(prev => ({ ...prev, [key]: { loading: true, lessons: [], error: null } }))
    try {
      const { data } = await api.get(`/progress/course/${courseId}?student_id=${studentId}`)
      setLessonDetail(prev => ({
        ...prev,
        [key]: { loading: false, lessons: data.lesson_progress || [], error: null },
      }))
    } catch (error) {
      console.error('Error fetching lesson detail:', error)
      setLessonDetail(prev => ({
        ...prev,
        [key]: { loading: false, lessons: [], error: 'Failed to load lessons' },
      }))
    }
  }, [])

  const toggleExpand = useCallback((studentId, courseId) => {
    const key = `${studentId}_${courseId}`
    setExpandedKey(prev => {
      if (prev === key) return null
      // Fetch detail on expand (if not already loaded)
      if (!lessonDetail[key]) fetchLessonDetail(studentId, courseId)
      return key
    })
  }, [lessonDetail, fetchLessonDetail])

  // ── Toggle complete / undo ─────────────────────────────────────────────────

  const handleToggle = useCallback(async (studentId, courseId, lesson, markComplete) => {
    const key = `${studentId}_${lesson.lesson_id}`
    const scheduleKey = `${studentId}_${courseId}`

    // Track in-flight
    setToggling(prev => { const n = new Set(prev); n.add(key); return n })

    // Snapshot for revert
    const prevSchedules = schedules
    const prevDetail = lessonDetail[scheduleKey]

    // Optimistic update — schedule summary
    setSchedules(prev => prev.map(s => {
      if (s.student_id !== studentId || s.course_id !== courseId) return s
      // If we have full lesson detail, recompute accurately; else best-effort collapsed update
      const detail = lessonDetail[scheduleKey]
      if (detail?.lessons?.length) {
        const updatedLessons = detail.lessons.map(l =>
          l.lesson_id === lesson.lesson_id
            ? { ...l, status: markComplete ? 'completed' : 'in_progress', completed_at: markComplete ? new Date().toISOString() : null }
            : l
        )
        return recomputeFromLessons(s, updatedLessons)
      }
      return applyCollapsedToggle(s, lesson.lesson_id, markComplete)
    }))

    // Optimistic update — expanded lesson detail (if loaded)
    if (lessonDetail[scheduleKey]?.lessons?.length) {
      setLessonDetail(prev => ({
        ...prev,
        [scheduleKey]: {
          ...prev[scheduleKey],
          lessons: prev[scheduleKey].lessons.map(l =>
            l.lesson_id === lesson.lesson_id
              ? { ...l, status: markComplete ? 'completed' : 'in_progress', completed_at: markComplete ? new Date().toISOString() : null }
              : l
          ),
        },
      }))
    }

    try {
      const endpoint = markComplete ? 'complete' : 'uncomplete'
      await api.post(`/schedule/lessons/${lesson.lesson_id}/${endpoint}`, {
        student_ids: [studentId],
      })
      toast.success(
        markComplete
          ? `Marked "${lesson.lesson_title}" complete`
          : `Reverted "${lesson.lesson_title}"`
      )
    } catch (error) {
      // Revert optimistic updates
      setSchedules(prevSchedules)
      if (prevDetail) setLessonDetail(prev => ({ ...prev, [scheduleKey]: prevDetail }))
      const errMsg = error.response?.data?.error || error.response?.data?.message
        || (markComplete ? 'Failed to mark lesson complete' : 'Failed to revert lesson')
      toast.error(errMsg)
    } finally {
      setToggling(prev => { const n = new Set(prev); n.delete(key); return n })
    }
  }, [schedules, lessonDetail])

  // ── Derived data ───────────────────────────────────────────────────────────

  const courses = useMemo(() => {
    const map = new Map()
    schedules.forEach(s => {
      if (!map.has(s.course_id)) {
        map.set(s.course_id, { id: s.course_id, title: s.course_title, code: s.course_code })
      }
    })
    return Array.from(map.values())
  }, [schedules])

  const filteredSchedules = useMemo(() => {
    let list = schedules
    // Course filter
    if (courseFilter !== 'all') {
      list = list.filter(s => String(s.course_id) === String(courseFilter))
    }
    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(s =>
        `${s.student_first_name} ${s.student_last_name}`.toLowerCase().includes(q) ||
        String(s.student_number || '').toLowerCase().includes(q)
      )
    }
    // Sort
    const sorted = [...list]
    if (sortBy === 'name') {
      sorted.sort((a, b) =>
        `${a.student_first_name} ${a.student_last_name}`.localeCompare(`${b.student_first_name} ${b.student_last_name}`)
      )
    } else if (sortBy === 'progress') {
      sorted.sort((a, b) => pctOf(b) - pctOf(a))
    } else if (sortBy === 'status') {
      // Not-started first, then in-progress, then completed
      const rank = (s) => {
        if (s.total_lessons > 0 && s.completed_lessons === s.total_lessons) return 2
        if (s.current_lesson) return 1
        return 0
      }
      sorted.sort((a, b) => rank(a) - rank(b))
    }
    return sorted
  }, [schedules, courseFilter, searchQuery, sortBy])

  const stats = useMemo(() => {
    const uniqueStudents = new Set(schedules.map(s => s.student_id)).size
    const withLessons = schedules.filter(s => s.total_lessons > 0)
    const avgCompletion = withLessons.length > 0
      ? Math.round(withLessons.reduce((sum, s) => sum + pctOf(s), 0) / withLessons.length)
      : 0
    const inProgress = schedules.filter(s => s.current_lesson).length
    const completed = schedules.filter(s => s.total_lessons > 0 && s.completed_lessons === s.total_lessons).length
    return { uniqueStudents, avgCompletion, inProgress, completed }
  }, [schedules])

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner label="Loading student progress" />

  return (
    <div className="space-y-6">
      {/* ── Page header (no date — decoupled from events) ── */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-0.5">Trainer Portal</span>
          <h1 className="font-display text-xl text-white font-light">Student Progress</h1>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8">
        {[
          { label: 'Students',        value: stats.uniqueStudents },
          { label: 'Avg Completion',  value: `${stats.avgCompletion}%` },
          { label: 'In Progress',     value: stats.inProgress },
          { label: 'Completed',       value: stats.completed },
        ].map(({ label, value }) => (
          <div key={label} className="cinema-stat">
            <span className="cinema-stat-label">{label}</span>
            <span className="cinema-stat-value">{value}</span>
          </div>
        ))}
      </div>

      {/* ── Events (date-scoped) ── */}
      <div className="pt-8 border-t border-white/5">
        <div className="cinema-section-header flex-wrap gap-3">
          <h3 className="cinema-section-title">Events</h3>
          <div className="flex items-center gap-2 border border-white/10 px-3 py-2">
            <Calendar size={14} className="text-cinema-muted shrink-0" aria-hidden="true" />
            <label htmlFor="events-date-input" className="font-sans text-xs text-cinema-muted font-medium">Date</label>
            <input
              id="events-date-input"
              type="date"
              value={eventsDate}
              onChange={(e) => setEventsDate(e.target.value)}
              aria-label="Events date"
              className="bg-transparent text-white text-sm font-sans focus:outline-none"
            />
          </div>
        </div>

        {eventsLoading ? (
          <p className="font-sans text-xs sm:text-sm text-cinema-muted py-4">Loading events…</p>
        ) : trainerEvents.length > 0 ? (
          <div className="cinema-list">
            {trainerEvents.map(event => {
              const acc = eventTypeAccent(event.event_type)
              return (
                <div key={event.id} className="cinema-list-row">
                  <div className={`w-1 h-8 shrink-0 ${acc.bar}`} />
                  <div className="min-w-[68px]">
                    <p className="font-mono text-xs sm:text-sm text-white font-medium">
                      {new Date(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="font-mono text-xs text-cinema-muted">
                      {new Date(event.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm sm:text-base text-white font-medium truncate">{event.title}</p>
                    {event.course_name && (
                      <p className="font-sans text-xs text-cinema-muted truncate mt-0.5">
                        {event.course_code} — {event.course_name}
                      </p>
                    )}
                  </div>
                  {event.location && (
                    <span className="flex items-center gap-1 font-sans text-xs sm:text-sm text-cinema-muted shrink-0 font-medium">
                      <MapPin size={12} aria-hidden="true" /> {event.location}
                    </span>
                  )}
                  <span className={`px-2.5 py-0.5 font-sans text-xs font-medium capitalize ${acc.bg} ${acc.text} border ${acc.border} rounded-sm`}>
                    {event.event_type}
                  </span>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="font-sans text-xs sm:text-sm text-cinema-muted py-4">No events for this date.</p>
        )}
      </div>

      {/* ── Student list ── */}
      {schedules.length === 0 ? (
        <div className="cinema-empty">
          <Users size={36} className="mx-auto mb-4 text-cinema-muted/20" aria-hidden="true" />
          <p className="font-sans text-sm text-cinema-muted">No students enrolled in your courses yet</p>
        </div>
      ) : (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header flex-wrap gap-3">
            <h3 className="cinema-section-title">Students</h3>
          </div>

          {/* Toolbar: search + course filter + sort */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="flex items-center gap-2 border border-white/10 px-3 py-2 flex-1 min-w-[180px]">
              <Search size={13} className="text-cinema-muted shrink-0" aria-hidden="true" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or student #…"
                aria-label="Search students"
                className="bg-transparent text-white text-sm font-sans focus:outline-none w-full"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="text-cinema-muted hover:text-white transition-colors shrink-0"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            {courses.length > 1 && (
              <select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                aria-label="Filter by course"
                className="bg-transparent border border-white/10 text-white text-sm font-sans px-3 py-2 focus:outline-none cursor-pointer"
              >
                <option value="all">All courses</option>
                {courses.map(c => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            )}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Sort students"
              className="bg-transparent border border-white/10 text-white text-sm font-sans px-3 py-2 focus:outline-none cursor-pointer"
            >
              <option value="name">Sort: Name</option>
              <option value="progress">Sort: % Complete</option>
              <option value="status">Sort: Status</option>
            </select>
          </div>

          {/* Result count */}
          <p className="font-sans text-xs sm:text-sm text-cinema-muted mb-3 font-medium">
            {filteredSchedules.length} enrollment{filteredSchedules.length !== 1 ? 's' : ''}
            {filteredSchedules.length !== schedules.length && ` of ${schedules.length}`}
          </p>

          {/* List */}
          {filteredSchedules.length === 0 ? (
            <p className="font-sans text-xs sm:text-sm text-cinema-muted py-8 text-center">
              No students match your search.
            </p>
          ) : (
            <div className="cinema-list">
              {filteredSchedules.map((schedule) => {
                const pct = pctOf(schedule)
                const key = `${schedule.student_id}_${schedule.course_id}`
                const isExpanded = expandedKey === key
                const detail = lessonDetail[key]
                const allDone = !schedule.current_lesson && !schedule.next_lesson
                  && (schedule.upcoming_lessons?.length === 0)
                  && schedule.total_lessons > 0

                return (
                  <StudentRow
                    key={key}
                    schedule={schedule}
                    pct={pct}
                    isExpanded={isExpanded}
                    detail={detail}
                    toggling={toggling}
                    allDone={allDone}
                    onToggleExpand={() => toggleExpand(schedule.student_id, schedule.course_id)}
                    onToggle={(lesson, markComplete) =>
                      handleToggle(schedule.student_id, schedule.course_id, lesson, markComplete)
                    }
                    onRetryFetch={() => fetchLessonDetail(schedule.student_id, schedule.course_id)}
                    headerRef={(el) => { rowHeaderRefs.current[key] = el }}
                  />
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── StudentRow (collapsed + expanded) ────────────────────────────────────────

function StudentRow({ schedule, pct, isExpanded, detail, toggling, allDone, onToggleExpand, onToggle, onRetryFetch, headerRef }) {
  const studentName = `${schedule.student_first_name} ${schedule.student_last_name}`
  const Chevron = isExpanded ? ChevronDown : ChevronRight

  return (
    <div className="cinema-list-row flex-col !items-stretch py-4 transition-all">
      {/* Collapsed header */}
      <div className="flex items-start gap-3">
        <button
          onClick={onToggleExpand}
          aria-label={isExpanded ? `Collapse lessons for ${studentName}` : `Expand lessons for ${studentName}`}
          aria-expanded={isExpanded}
          className="mt-0.5 text-cinema-muted hover:text-cinema-glow transition-colors shrink-0"
        >
          <Chevron size={16} />
        </button>

        <div className="flex-1 min-w-0" ref={headerRef}>
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <p className="font-sans text-base text-white font-medium">{studentName}</p>
            <span className="font-mono text-xs sm:text-sm text-cinema-muted">#{schedule.student_number}</span>
          </div>
          <p className="font-sans text-xs sm:text-sm text-cinema-muted mb-2 font-medium">{schedule.course_title}</p>

          {/* Progress bar (a11y) */}
          <div className="flex items-center gap-2 mb-3">
            <div
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${studentName} ${schedule.course_title} completion: ${pct}%`}
              className="flex-1 bg-white/5 h-1.5"
            >
              <div className="h-1.5 bg-cinema-glow transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            <span className="font-mono text-xs sm:text-sm text-cinema-muted shrink-0 font-medium">
              {pct}% ({schedule.completed_lessons}/{schedule.total_lessons})
            </span>
          </div>

          {/* Current lesson — one-click shortcut */}
          {schedule.current_lesson && (
            <LessonShortcut
              lesson={schedule.current_lesson}
              status="in_progress"
              onMarkComplete={() => onToggle(schedule.current_lesson, true)}
              disabled={toggling.has(`${schedule.student_id}_${schedule.current_lesson.lesson_id}`)}
            />
          )}

          {/* Next lesson — one-click shortcut (only if no current) */}
          {schedule.next_lesson && (
            <LessonShortcut
              lesson={schedule.next_lesson}
              status="not_started"
              onMarkComplete={() => onToggle(schedule.next_lesson, true)}
              disabled={toggling.has(`${schedule.student_id}_${schedule.next_lesson.lesson_id}`)}
            />
          )}

          {/* All done */}
          {allDone && (
            <div className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-400 shrink-0" aria-hidden="true" />
              <p className="font-sans text-xs sm:text-sm text-green-400 font-medium">All lessons completed</p>
            </div>
          )}
        </div>
      </div>

      {/* Expanded: full lesson list */}
      {isExpanded && (
        <ExpandedLessonList
          schedule={schedule}
          detail={detail}
          toggling={toggling}
          onToggle={onToggle}
          onRetry={onRetryFetch}
        />
      )}
    </div>
  )
}

// ── Collapsed-row lesson shortcut (current / next) ───────────────────────────

function LessonShortcut({ lesson, status, onMarkComplete, disabled }) {
  const meta = STATUS_META[status] || STATUS_META.not_started
  const { Icon, text, border } = meta
  const isCurrent = status === 'in_progress'
  const toggleKey = `${lesson.lesson_id}` // for disabled state

  return (
    <div className={`border-l-2 ${border} pl-3 mb-2 flex items-center justify-between gap-3`}>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Icon size={13} className={`${text} shrink-0`} aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-sans text-sm sm:text-base text-white font-medium truncate">{lesson.lesson_title}</p>
          <p className="font-sans text-xs sm:text-sm text-cinema-muted font-medium">
            <span className={text}>{meta.label}</span>
            {' · '}Day {lesson.day_number}
          </p>
        </div>
      </div>
      <button
        onClick={onMarkComplete}
        disabled={disabled}
        className="cinema-btn-primary text-xs sm:text-sm px-3.5 py-1.5 min-h-0 shrink-0 disabled:opacity-50 font-medium"
        aria-label={`Mark "${lesson.lesson_title}" complete`}
      >
        {disabled ? '…' : 'Mark Complete'}
      </button>
    </div>
  )
}

// ── Expanded lesson list (grouped by module) ─────────────────────────────────

function ExpandedLessonList({ schedule, detail, toggling, onToggle, onRetry }) {
  if (!detail || detail.loading) {
    return (
      <div className="pl-7 pt-3 pb-2">
        <p className="font-sans text-xs sm:text-sm text-cinema-muted">Loading lessons…</p>
      </div>
    )
  }
  if (detail.error) {
    return (
      <div className="pl-7 pt-3 pb-2">
        <p className="font-sans text-xs sm:text-sm text-red-400 mb-2">{detail.error}</p>
        <button onClick={onRetry} className="cinema-btn-ghost text-xs sm:text-sm px-3.5 py-1.5 min-h-0 font-medium">Retry</button>
      </div>
    )
  }
  if (!detail.lessons || detail.lessons.length === 0) {
    return (
      <div className="pl-7 pt-3 pb-2">
        <p className="font-sans text-xs sm:text-sm text-cinema-muted">No published lessons for this course.</p>
      </div>
    )
  }

  // Group by module
  const modules = new Map()
  for (const l of detail.lessons) {
    const mKey = l.module_number
    if (!modules.has(mKey)) {
      modules.set(mKey, {
        module_number: l.module_number,
        module_title: l.module_title,
        week_number: l.week_number,
        lessons: [],
      })
    }
    modules.get(mKey).lessons.push(l)
  }

  return (
    <div className="pl-7 pt-3 mt-2 border-t border-white/5 space-y-4">
      {Array.from(modules.values()).map(mod => {
        const modCompleted = mod.lessons.filter(l => l.status === 'completed').length
        return (
          <div key={mod.module_number}>
            <div className="flex items-center gap-2 mb-2">
              <Book size={13} className="text-cinema-muted shrink-0" aria-hidden="true" />
              <p className="font-sans text-xs sm:text-sm text-white font-medium">
                {mod.module_title}
              </p>
              <span className="font-sans text-xs text-cinema-muted">
                · Week {mod.week_number} · {modCompleted}/{mod.lessons.length}
              </span>
            </div>
            <div className="space-y-1.5">
              {mod.lessons.map(lesson => (
                <LessonToggleRow
                  key={lesson.lesson_id}
                  lesson={lesson}
                  studentId={schedule.student_id}
                  toggling={toggling}
                  onToggle={onToggle}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Per-lesson toggle row (in expanded view) ─────────────────────────────────

function LessonToggleRow({ lesson, studentId, toggling, onToggle }) {
  const status = lesson.status || 'not_started'
  const meta = STATUS_META[status] || STATUS_META.not_started
  const { Icon, text, bg, border } = meta
  const isCompleted = status === 'completed'
  const toggleKey = `${studentId}_${lesson.lesson_id}`
  const isToggling = toggling.has(toggleKey)

  return (
    <div className="flex items-center gap-3 py-1.5 group">
      <Icon size={14} className={`${text} shrink-0`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="font-sans text-sm sm:text-base text-white font-medium truncate">
          {lesson.lesson_title}
          <span className="font-sans text-xs sm:text-sm text-cinema-muted font-normal"> · Day {lesson.day_number}</span>
        </p>
      </div>
      <span className={`px-2.5 py-0.5 font-sans text-xs font-medium ${bg} ${text} border ${border} shrink-0 rounded-sm`}>
        {meta.label}
      </span>
      <button
        onClick={() => onToggle(lesson, !isCompleted)}
        disabled={isToggling}
        className={
          isCompleted
            ? 'cinema-btn-ghost text-xs sm:text-sm px-3.5 py-1.5 min-h-0 shrink-0 disabled:opacity-50 font-medium'
            : 'cinema-btn-primary text-xs sm:text-sm px-3.5 py-1.5 min-h-0 shrink-0 disabled:opacity-50 font-medium'
        }
        aria-label={
          isCompleted
            ? `Undo completion of "${lesson.lesson_title}"`
            : `Mark "${lesson.lesson_title}" complete`
        }
      >
        {isToggling ? '…' : isCompleted ? (
          <span className="flex items-center gap-1"><RotateCcw size={12} /> Undo</span>
        ) : 'Mark Complete'}
      </button>
    </div>
  )
}
